import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  Pagination,
  List,
  Grid,
  Card,
  CardContent,
  CardMedia,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { getTrafficCameras, getTrafficSignals , getSpeedDetectors, getTrafficMessageBoards} from '../../api/api';
import { AssetType } from '../../services/AppConstant';

function Assets() {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [trafficCameras, setTrafficCameras] = useState([]);
  const [trafficSignals, setTrafficSignals] = useState([]);
  const [speedDetectors, setSpeedDetectors] = useState([]);
  const [messageBoards, setMessageBoards] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Default number of items per page
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);


  // Function to fetch data from the API
  const fetchData = async () => {
    setLoading(true);
    try {
      let endpoint;
      let response;

      // Determine the endpoint based on the active tab
      switch (activeTab) {
        case 0:
          endpoint = getTrafficCameras;
          break;
        case 1:
          endpoint = getTrafficSignals;
          break;
        case 2:
          endpoint = getSpeedDetectors;
          break;
        case 3:
          endpoint = getTrafficMessageBoards;
          break;
        default:
          return;
      }

      response = await endpoint({ search: searchTerm, page, limit: pageSize });

      // Update state based on active tab
      if (activeTab === 0) {
        setTrafficCameras(response.data.data);
      } else if (activeTab === 1) {
        setTrafficSignals(response.data.data);
      } else if (activeTab === 2) {
        setSpeedDetectors(response.data.data);
      } else if (activeTab === 3) {
        setMessageBoards(response.data.data);
      }

      setTotalRecords(response.data.total); // Set total records from API
      setTotalPages(Math.ceil(response.data.total / pageSize)); // Calculate total pages
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  // Fetch data when the component mounts or when searchTerm, page, or activeTab changes
  useEffect(() => {
    fetchData();
  }, [searchTerm, page,pageSize, activeTab]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(1); // Reset to page 1 when switching tabs
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1); // Reset to page 1 when changing page size
  };

   // Handle rendering for each asset type
   const renderAssetList = (assets, type) =>
    assets.map((asset, index) => (
      <Card key={index} sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <CardContent>
              <Typography variant="h6">{asset['Location Name'] || asset['Primary Street']}</Typography>
              <Typography variant="body1">
                <strong>ID:</strong> {asset['Signal ID'] || asset['Camera ID'] || asset._id}
              </Typography>
              <Typography variant="body1">
                <strong>Primary Street:</strong> {asset['Primary Street']}
              </Typography>
              <Typography variant="body1">
                <strong>Cross Street:</strong> {asset['Cross Street']}
              </Typography>
              <Typography variant="body1">
                <strong>Location:</strong>{' '}
                {`[${asset.Location.coordinates[1]}, ${asset.Location.coordinates[0]}]`}
              </Typography>
              {type === 'Traffic Message Board' && (
                <Typography variant="body1">
                  <strong>Current Message:</strong> {asset['Current Message']}
                </Typography>
              )}

              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mr: 2 }}
                  onClick={() =>
                    window.location.href = `/asset/${type}/${asset._id}`
                  }
                >
                  Show More Details
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleViewOnMap(asset)}
                >
                  View on Map
                </Button>
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Card>
    ));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Traffic Assets
      </Typography>
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab label="Traffic Cameras" />
        <Tab label="Traffic Signals" />
        <Tab label="Speed Detectors" />
        <Tab label="Message Boards" />
      </Tabs>

      <Box sx={{ mt: 3 }}>
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          sx={{ mb: 2 }}
        />
        <Button variant="contained" color="primary" onClick={fetchData} disabled={loading}>
          {loading ? 'Loading...' : 'Search'}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && (
          <List>
           {trafficCameras.map((camera, index) => (
          <Card key={index} sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <CardMedia
                  component="img"
                  height="150"
                  image={camera['Screenshot Address'] || 'default_image.jpg'}
                  alt="Camera Screenshot"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <CardContent>
                  <Typography variant="h6">
                    {camera['Location Name']}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Camera ID:</strong> {camera['Camera ID']}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Primary Street:</strong> {camera['Primary Street']}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Cross Street:</strong> {camera['Cross Street']}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Camera Status:</strong> {camera['Camera Status']}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Location:</strong>{' '}
                    {`[${camera.Location.coordinates[1]}, ${camera.Location.coordinates[0]}]`}
                  </Typography>

                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mr: 2 }}
                      onClick={() => window.location.href = `/asset/${AssetType.CAMERA}/${camera._id}`}
                    >
                      Show More Details
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleViewOnMap(camera)}
                    >
                      View on Map
                    </Button>
                  </Box>
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        ))}
          </List>
        ) } { activeTab === 1 && (
            <List>
            {trafficSignals.map((signal, index) => (
              <Card key={index} sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <CardContent>
                      <Typography variant="h6">
                        {signal['Location Name']}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Signal ID:</strong> {signal['Signal ID']}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Primary Street:</strong> {signal['Primary St']}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Cross Street:</strong> {signal['Cross St']}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Signal Type:</strong> {signal['Signal Type']}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Signal Status:</strong> {signal['Signal Status']}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Location:</strong>{' '}
                        {`[${signal.Location.coordinates[1]}, ${signal.Location.coordinates[0]}]`}
                      </Typography>
    
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{ mr: 2 }}
                          onClick={() => window.location.href = `/asset/${AssetType.TRAFFIC_SIGNAL}/${signal._id}`}
                        >
                          Show More Details
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleViewOnMap(signal)}
                        >
                          View on Map
                        </Button>
                      </Box>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            ))}
          </List>
        )}
           {activeTab === 2 && renderAssetList(speedDetectors, 'Speed Detector')}
        {activeTab === 3 && renderAssetList(messageBoards, 'Traffic Message Board')}
     
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
          Total Records: {totalRecords} | Page Size: 
          <FormControl sx={{ ml: 1 }}>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Page Size' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Typography>
      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        color="primary"
        sx={{ mt: 3 }}
      />
    </Box>
  );

   // Function to handle "Show More Details" button click
   function handleShowMoreDetails(camera) {
    alert(`Showing more details for Camera ID: ${camera['Camera ID']}`);
    // Implement your logic to show more details
  }

  // Function to handle "View on Map" button click
  function handleViewOnMap(camera) {
    alert(`Viewing on map: [${camera.Location.coordinates[1]}, ${camera.Location.coordinates[0]}]`);
    // Implement your logic to display the camera on a map
  }
}

export default Assets;
